import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { createSlug } from '../utils/slug';
import ListingImage from '../components/ListingImage';

const Cart: React.FC = () => {
  const { cartItems, removeFromCart, updateQuantity, clearCart, getTotalPrice, validateCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    validateCart();
  }, [validateCart]);

  const formatPrice = (price: number | null) => {
    if (!price) return 'Free';
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }
    // Convert cart items to listings array for checkout
    const listings = cartItems.flatMap(item => 
      Array(item.quantity).fill(null).map(() => ({ ...item.listing }))
    );
    navigate('/checkout', { state: { listings } });
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-12 text-center">
            <i className="ri-shopping-cart-line text-4xl sm:text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">Add items to your cart to get started!</p>
            <Link
              to="/"
              className="inline-block bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-3 rounded-lg transition text-sm sm:text-base"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = getTotalPrice();
  const deliveryFee = 2.99;
  const total = subtotal + deliveryFee;

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Shopping Cart</h1>
          <button
            onClick={clearCart}
            className="text-red-600 hover:text-red-700 font-medium text-sm self-start sm:self-auto"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {cartItems.map((item) => {
              const listing = item.listing;
              const maxQuantity = listing.quantity || Infinity;
              
              return (
                <div key={listing.id} className="bg-white rounded-lg shadow-md p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Link to={`/listings/${listing.id}`} className="flex-shrink-0 self-center sm:self-start">
                    <ListingImage
                      src={listing.image_urls?.[0] || listing.image_url || ''}
                      size="thumb"
                      alt={listing.title}
                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/listings/${listing.id}`} className="block">
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 hover:text-green-600 transition mb-1 line-clamp-2">
                        {listing.title}
                      </h3>
                    </Link>
                    {listing.store_name ? (
                      <Link
                        to={`/stores/${createSlug(listing.store_name)}`}
                        className="text-xs sm:text-sm text-gray-600 hover:text-green-600 transition"
                      >
                        <i className="ri-store-line mr-1"></i>
                        {listing.store_name}
                      </Link>
                    ) : (
                      <Link
                        to={`/sellers/${listing.user_id}`}
                        className="text-xs sm:text-sm text-gray-600 hover:text-green-600 transition"
                      >
                        <i className="ri-user-line mr-1"></i>
                        {listing.seller_name}
                      </Link>
                    )}
                    <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center justify-between sm:justify-start gap-3">
                        <span className="text-base sm:text-lg font-bold text-gray-900">
                          {formatPrice(listing.price)}
                        </span>
                        <div className="flex items-center space-x-2 border border-gray-300 rounded-lg">
                          <button
                            onClick={() => updateQuantity(listing.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="px-2 py-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            <i className="ri-subtract-line text-sm sm:text-base"></i>
                          </button>
                          <span className="px-2 sm:px-3 py-1 font-semibold min-w-[2rem] sm:min-w-[3rem] text-center text-sm sm:text-base">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(listing.id, item.quantity + 1)}
                            disabled={item.quantity >= maxQuantity}
                            className="px-2 py-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            <i className="ri-add-line text-sm sm:text-base"></i>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3">
                        <span className="text-base sm:text-lg font-bold text-gray-900">
                          {formatPrice((Number(listing.price) || 0) * item.quantity)}
                        </span>
                        <button
                          onClick={() => removeFromCart(listing.id)}
                          className="text-red-600 hover:text-red-700 transition"
                          title="Remove from cart"
                        >
                          <i className="ri-delete-bin-line text-lg sm:text-xl"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 lg:sticky lg:top-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm sm:text-base text-gray-700">
                  <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                  <span className="font-semibold">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base text-gray-700">
                  <span>Delivery</span>
                  <span className="font-semibold">{formatPrice(deliveryFee)}</span>
                </div>
                <p className="text-xs text-gray-500">Tax calculated at checkout</p>
                <div className="border-t border-gray-200 pt-3 flex justify-between text-base sm:text-lg font-bold text-gray-900">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 sm:py-3 rounded-lg transition shadow-md hover:shadow-lg text-sm sm:text-base"
              >
                Proceed to Checkout
              </button>
              <Link
                to="/"
                className="block text-center text-gray-600 hover:text-gray-900 mt-4 text-xs sm:text-sm"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;

